import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import { kycService } from '../../../services/kycService';

const KYCPage: React.FC = () => {
  const [status, setStatus] = useState<'PENDING'|'APPROVED'|'REJECTED'>('PENDING');
  const [documents, setDocuments] = useState<any[]>([]);
  const [docType, setDocType] = useState<'ID_CARD'|'PASSPORT'|'ADDRESS_PROOF'|'TAX_FORM'>('ID_CARD');
  const [fileUrl, setFileUrl] = useState('');
  const [note, setNote] = useState('');
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    const res = await kycService.getMyStatus();
    setStatus(res.status);
    setDocuments(res.documents || []);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!fileUrl) { alert('Provide a document URL'); return; }
    await kycService.upload({ docType, fileUrl, note: note || undefined });
    setFileUrl('');
    setNote('');
    await load();
  };

  const startStripeVerification = async () => {
    try {
      setVerifying(true);
      
      // Load Stripe.js
      const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      if (!stripeKey) {
        alert('Stripe is not configured');
        return;
      }

      // Create verification session on backend
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`}/api/kyc/create-verification-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create verification session');
      }

      const { clientSecret } = await response.json();

      // Load Stripe dynamically
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(stripeKey);
      
      if (!stripe) {
        alert('Failed to load Stripe');
        return;
      }

      // Show Stripe Identity verification modal
      const result = await stripe.verifyIdentity(clientSecret);

      if (result.error) {
        alert(result.error.message);
      } else {
        alert('Identity verification submitted successfully! We will review your documents shortly.');
        await load();
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      alert(error.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">KYC Verification</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Current Status</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{status}</div>
            <div className="text-xs text-gray-500 mt-2">Upload the required documents to verify your identity and enable payouts.</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 mb-4">Quick Verification</div>
            <Button 
              onClick={startStripeVerification} 
              disabled={verifying}
              className="w-full"
            >
              {verifying ? 'Opening Verification...' : 'Verify with Stripe Identity'}
            </Button>
            <div className="text-xs text-gray-500 mt-2">
              Fastest way to verify - uses government-issued ID and selfie
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Submit Document</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select value={docType} onChange={(e)=>setDocType(e.target.value as any)} className="w-full px-3 py-2 border rounded">
                <option value="ID_CARD">ID Card</option>
                <option value="PASSPORT">Passport</option>
                <option value="ADDRESS_PROOF">Address Proof</option>
                <option value="TAX_FORM">Tax Form</option>
              </select>
            </div>
            <FormInput label="File URL" value={fileUrl} onChange={(e)=>setFileUrl(e.target.value)} placeholder="https://..." />
          </div>
          <FormInput label="Note (optional)" value={note} onChange={(e)=>setNote(e.target.value)} />
          <div className="mt-3">
            <Button onClick={submit}>Upload</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={4}>No documents submitted.</td></tr>
                ) : documents.map(d => (
                  <tr key={d.id}>
                    <td className="px-4 py-3 text-sm text-gray-700">{new Date(d.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{d.docType}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        d.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                        d.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600 underline"><a href={d.fileUrl} target="_blank">View</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KYCPage;



